<template>
  <v-dialog
    v-model="dialog"
    class="generic-popup"
    :content-class="scrollable ? 'generic-popup-scrollable' : 'generic-popup-not-scrollable'"
    :disabled="disabled"
    :persistent="persistent"
    :style="{ zIndex: z_index }"
    :width="width"
  >
    <template #activator="{ props }">
      <div v-bind="props">
        <slot name="trigger" />
      </div>
    </template>

    <div
      class="dialog"
      :class="{'dialog--overflow': add_overflow}"
    >
      <div class="header">
        <div class="title">
          <slot name="title">
            <h4 class="nds-text-h4">
              {{ title }}
            </h4>
          </slot>
        </div>

        <div class="close">
          <svg-element
            icon="close"
            size="12"
            @click="closeModal()"
          />
        </div>
      </div>

      <div
        v-if="has_slot_sub_title"
        class="sub-title"
      >
        <slot name="sub_title" />
      </div>

      <div
        v-if="separator"
        class="separator"
      />

      <div
        class="content"
        :class="{
          'content--no-separator': !separator,
          'content--overflow': add_overflow,
        }"
      >
        <slot name="content" />
      </div>

      <div
        v-equal-cta
        class="footer"
      >
        <div
          v-if="has_slot_pre_actions"
          class="pre-actions"
        >
          <slot name="pre_actions" />
        </div>

        <div
          v-if="has_slot_actions"
          class="actions"
        >
          <slot name="actions" />
        </div>

        <div
          v-if="has_slot_post_actions"
          class="post-actions"
        >
          <slot name="post_actions" />
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import SvgElement from '@/components/elements/SvgElement';

const CTA_SELECTOR = '.button:not(.button--icon-only)';
const CTA_WIDTH_VARIABLE = '--cta-width';
const EQUAL_WIDTH_CTA_COUNT = 2;

function equalizeCtaWidth(footer) {
  footer.style.removeProperty(CTA_WIDTH_VARIABLE);

  const cta_list = Array.from(footer.querySelectorAll(CTA_SELECTOR));

  if (cta_list.length !== EQUAL_WIDTH_CTA_COUNT) {
    return;
  }

  const widest_cta_width = Math.max(...cta_list.map((cta) => cta.offsetWidth));

  if (widest_cta_width > 0) {
    footer.style.setProperty(CTA_WIDTH_VARIABLE, `${widest_cta_width}px`);
  }
}

export default {
  name: 'GenericPopup',
  components: {
    SvgElement,
  },
  directives: {
    equalCta: {
      mounted: equalizeCtaWidth,
      updated: equalizeCtaWidth,
    },
  },
  props: {
    add_overflow: {
      default: true,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    // eslint-disable-next-line vue/prop-name-casing
    modelValue: Boolean,
    persistent: {
      default: false,
      type: Boolean,
    },
    separator: {
      default: true,
      type: Boolean,
    },
    scrollable: {
      default: false,
      type: Boolean,
    },
    title: String,
    width: {
      default: '500',
      type: String,
    },
    z_index: Number,
    // eslint-disable-next-line vue/prop-name-casing
    onClose: Function,
  },
  emits: [
    'change',
    'close',
    'update:modelValue',
  ],
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(new_value) {
        this.$emit('update:modelValue', new_value);
        this.$emit('change');
      },
    },
    has_slot_actions() {
      return this.$slots.actions !== undefined;
    },
    has_slot_post_actions() {
      return this.$slots.post_actions !== undefined;
    },
    has_slot_pre_actions() {
      return this.$slots.pre_actions !== undefined;
    },
    has_slot_sub_title() {
      return this.$slots.sub_title !== undefined;
    },
  },
  watch: {
    dialog(value) {
      if (!value) {
        if (this.onClose) {
          this.onClose();
        }

        this.closeModal();
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');

      this.dialog = false;
    },
  },
};
</script>

<style lang="scss">
  .generic-popup-not-scrollable {
    overflow-y: inherit !important;
  }

  .generic-popup-scrollable {
    overflow-y: auto !important;
    --v-scrollbar-offset: 0 !important;
  }
</style>

<style lang="scss" scoped>
@import '@/constants/variables.module.scss';

.actions {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.close {
  cursor: pointer;
  font-size: 0;

  svg {
    --color: #{$haiti};
  }
}

.content {
  min-height: 0;

  &--overflow {
    overflow: auto;
  }

  &--no-separator {
    margin-top: 8px;
  }
}

.dialog {
  background-color: $white;
  border: 2px solid $whisper;
  border-radius: 20px;
  box-shadow: 4px 4px 10px 0px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: 90vh;
  padding: 24px;

  &--overflow {
    overflow: hidden;
  }
}

.footer {
  :deep(.button:not(.button--icon-only)) {
    width: var(--cta-width, auto);
  }
}

.generic-popup {
  z-index: map-get($zindex, popup);
}

.header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.pre-actions {
  margin-top: 24px;
}

.separator {
  border-top: 1px solid $white-lavender;
}

.sub-title {
  margin-top: 16px;
}
</style>

<i18n src="@/locales/common.json"></i18n>
